import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { formatPhone } from 'utils/phone-util'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import Badge from 'components/Badge'
import {
    getBoldBorderTopStyle,
    getFinalTotalStyle,
    getTypographyStyle,
    getBorderTopStyle,
    getNameElements
} from 'utils/formatAccordion-utill'
import { pdsAnalytics } from 'analytics/pdsAnalytics'
import { MESSAGES } from '../../messages/messages'

const AccordionReusable = ({ customerInfo, isCustomerInfo, purchageDetails }) => {
    const { fullName = '', fullAddress = '', phone = '' } = customerInfo || {}
    let listKey = Date.now()

    const handleChange = (event, isExpanded) => {
        const eventValues = {
            linkType: 'expand',
            contentSubType: 'pds accordion',
            sateliteTrackId: 'moduleExpandContent'
        }
        if (isExpanded && isCustomerInfo) {
            pdsAnalytics.clickEvent('your information', eventValues)
        } else if (isExpanded && purchageDetails?.name) {
            pdsAnalytics.clickEvent(purchageDetails?.name, eventValues)
        }
    }

    const listDetails = (details, isSubDetails = false) => {
        let purchageList = (details || []).map((item, index) => (
            <React.Fragment key={listKey + item?.name}>
                <Box
                    sx={{
                        ml: isSubDetails ? 8 : 4
                    }}
                    data-testid={`${item?.name}-details-block`}
                >
                    <Box
                        sx={{
                            color: 'common.charcoal.main',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between'
                        }}
                        data-testid={`${item?.name}-details-row`}
                    >
                        <Box data-testid={`${item?.name}-details-name`}>
                            <Typography
                                tabIndex={0}
                                component="span"
                                sx={{
                                    typography: { xs: 'regularBody2', smd: 'regularBody1' }
                                }}
                            >
                                {item?.name}
                            </Typography>
                            {item?.disclosure && <Badge badgeContent={item?.disclosure} />}
                        </Box>

                        {item?.value && (
                            <Box
                                data-testid={`${item?.name}-details-value`}
                                sx={{
                                    minWidth: 'fit-content',
                                    ml: 2
                                }}
                            >
                                <Typography
                                    tabIndex={0}
                                    component="span"
                                    sx={{ typography: { xs: 'regularBody2', smd: 'regularBody1' } }}
                                >
                                    {item?.value}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {item?.details && item?.details.length > 0 && (
                        <Box data-testid={`${item?.name}-sub-accordion`} sx={{ pt: 4, display: 'grid', gap: 4 }}>
                            {listDetails(item?.details, true)}
                        </Box>
                    )}
                </Box>
            </React.Fragment>
        ))
        return purchageList
    }

    const boldBorderTopStyle = getBoldBorderTopStyle(purchageDetails)
    const finalTotalStyle = getFinalTotalStyle(purchageDetails)
    const typographyStyle = getTypographyStyle(purchageDetails)
    const borderTop = getBorderTopStyle(purchageDetails)
    const blockName = getNameElements(isCustomerInfo, purchageDetails?.name, purchageDetails?.mainTitle, 'block')
    const AccordionName = getNameElements(
        isCustomerInfo,
        purchageDetails?.name,
        purchageDetails?.mainTitle,
        'accordion'
    )

    return (
        <>
            {purchageDetails?.mainTitle && (
                <Typography
                    data-testid={`${purchageDetails?.mainTitle}-panel-header`}
                    sx={{
                        typography: { xs: 'mediumSubTitle', smd: 'mediumTitle' },
                        color: 'common.charcoal.main'
                    }}
                >
                    {purchageDetails?.mainTitle}
                </Typography>
            )}

            <Box
                data-testid={blockName}
                sx={{
                    borderBottom: purchageDetails?.style?.noBorderBottom ? 'none' : '1px solid #D1D5D9',
                    p: !isCustomerInfo ? '0 0 16px' : '0px',
                    ...boldBorderTopStyle,
                    ...finalTotalStyle,
                    ...borderTop
                }}
                className={Object.keys(finalTotalStyle).length > 0 && 'accordionBlock_total'}
            >
                {isCustomerInfo ? (
                    <Typography
                        data-testid={`your-information-panel-header`}
                        sx={{
                            mb: 2,
                            color: 'common.charcoal.main',
                            typography: { xs: 'mediumSubTitle', smd: 'mediumTitle' },
                            textTransform: 'capitalize'
                        }}
                    >
                        {MESSAGES.your_information}
                    </Typography>
                ) : (
                    <>
                        {purchageDetails?.name && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    color: 'common.charcoal.main',
                                    mb: purchageDetails?.details.length > 0 ? 2 : 4
                                }}
                                data-testid={`${purchageDetails?.name}-summary`}
                            >
                                <Box data-testid={`${purchageDetails?.name}-summary-name`}>
                                    <Typography
                                        tabIndex={0}
                                        component="span"
                                        sx={{
                                            typography: typographyStyle,
                                            color: 'common.charcoal.main'
                                        }}
                                    >
                                        {purchageDetails?.name}
                                    </Typography>
                                    {purchageDetails?.disclosure && (
                                        <Badge badgeContent={purchageDetails?.disclosure} />
                                    )}
                                </Box>

                                {purchageDetails?.value && (
                                    <Box
                                        data-testid={`${purchageDetails?.name}-summary-value`}
                                        sx={{
                                            minWidth: 'fit-content',
                                            ml: 2
                                        }}
                                    >
                                        <Typography
                                            tabIndex={0}
                                            component="span"
                                            sx={{
                                                typography: typographyStyle,
                                                color: 'common.charcoal.main'
                                            }}
                                        >
                                            {purchageDetails?.value}
                                            {purchageDetails?.includeAXZPlan && <>*</>}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {purchageDetails?.includeAXZPlan && (
                            <Box
                                sx={{
                                    typography: 'regularBody3',
                                    color: 'common.charcoal.main',
                                    mb: 2
                                }}
                            >
                                {MESSAGES.axzprice_part1 +
                                    ' ' +
                                    purchageDetails?.includeAXZPrice +
                                    ' ' +
                                    MESSAGES.axzprice_part2}
                            </Box>
                        )}
                    </>
                )}

                {(purchageDetails?.details.length > 0 || isCustomerInfo) && (
                    <Accordion elevation={0} data-testid={AccordionName} onChange={handleChange}>
                        {(purchageDetails?.name || isCustomerInfo) && (
                            <AccordionSummary
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography
                                    sx={{
                                        typography: 'boldBody2'
                                    }}
                                >
                                    {MESSAGES.details}
                                </Typography>
                                <FontAwesomeIcon className="detail-accordian" icon={faAngleDown} />
                            </AccordionSummary>
                        )}
                        <AccordionDetails>
                            {isCustomerInfo ? (
                                <Box
                                    sx={{ pt: 4 }}
                                    className="accordianDetailsSection"
                                    data-testid={'your-information-details'}
                                >
                                    <Typography
                                        sx={{
                                            typography: 'regularBody2',
                                            color: 'common.charcoal.main',
                                            textTransform: 'capitalize'
                                        }}
                                        data-testid="fullname"
                                    >
                                        {fullName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            typography: 'regularBody2',
                                            color: 'common.charcoal.main',
                                            textTransform: 'capitalize'
                                        }}
                                        data-testid="fulladdress"
                                    >
                                        {fullAddress}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            typography: 'regularBody2',
                                            color: 'common.charcoal.main',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        {formatPhone(phone)}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    className="accordianDetailsSection"
                                    sx={{ pt: purchageDetails?.name ? 4 : '0', display: 'grid', gap: 4 }}
                                    data-testid={
                                        purchageDetails?.name
                                            ? `${purchageDetails?.name}-details`
                                            : `${purchageDetails?.mainTitle}-details`
                                    }
                                >
                                    {listDetails(purchageDetails?.details)}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
        </>
    )
}

AccordionReusable.propTypes = {
    customerInfo: PropTypes.object,
    isCustomerInfo: PropTypes.bool,
    details: PropTypes.array,
    purchageDetails: PropTypes.object
}

export default AccordionReusable
